import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Download, X, Check, ArrowUpDown, Wallet, Paperclip, Upload } from 'lucide-react';

const API_URL = '';

export default function PettyCashTab() {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_expenses: 0, total_deposit: 0, cash_on_hand: 0 });
  const [form, setForm] = useState({ date: '', item: '', expenses: '', deposit: '', project_id: '', receipt_file: null });
  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sortAsc, setSortAsc] = useState(false);
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [loading, setLoading] = useState(true);
  const receiptInputs = useRef({});

  const loadEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/petty-cash`);
      setEntries(res.data.entries);
      setTotals({
        total_expenses: res.data.total_expenses,
        total_deposit: res.data.total_deposit,
        cash_on_hand: res.data.cash_on_hand,
      });
    } catch {
      toast.error('Failed to load petty cash entries');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects`);
      setProjects(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadEntries(); loadProjects(); }, [loadEntries, loadProjects]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date || !form.item) return toast.error('Date and Item are required');
    const expensesNum = parseFloat(form.expenses) || 0;
    const depositNum = parseFloat(form.deposit) || 0;
    if (!expensesNum && !depositNum) return toast.error('Enter an expense or deposit amount');
    // EXPENSE entries require project + receipt; deposits do not.
    if (expensesNum > 0) {
      if (!form.project_id) return toast.error('Project is required for expense entries');
      if (!form.receipt_file) return toast.error('Receipt is required for expense entries');
    }
    const proj = projects.find(p => p.id === form.project_id);
    try {
      const createRes = await axios.post(`${API_URL}/api/petty-cash`, {
        date: form.date,
        item: form.item,
        expenses: expensesNum,
        deposit: depositNum,
        project_id: form.project_id || null,
        project_name: proj?.project_name || null,
      });
      const newId = createRes.data.id;
      // For expenses, also upload the receipt in the same operation
      if (expensesNum > 0 && form.receipt_file && newId) {
        const fd = new FormData();
        fd.append('file', form.receipt_file);
        try {
          await axios.post(`${API_URL}/api/petty-cash/${newId}/upload-receipt`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          // Roll back the entry if the receipt upload fails so the rule remains enforced.
          await axios.delete(`${API_URL}/api/petty-cash/${newId}`).catch(() => {});
          throw err;
        }
      }
      setForm({ date: '', item: '', expenses: '', deposit: '', project_id: '', receipt_file: null });
      loadEntries();
      toast.success('Entry added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add entry');
    }
  };

  const handleUploadReceipt = async (entryId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/petty-cash/${entryId}/upload-receipt`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Receipt uploaded');
      loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload receipt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/petty-cash/${id}`);
      loadEntries();
      toast.success('Entry deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({ date: entry.date, item: entry.item, expenses: entry.expenses, deposit: entry.deposit, project_id: entry.project_id || '' });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async (id) => {
    const proj = projects.find(p => p.id === editForm.project_id);
    try {
      await axios.put(`${API_URL}/api/petty-cash/${id}`, {
        date: editForm.date,
        item: editForm.item,
        expenses: parseFloat(editForm.expenses) || 0,
        deposit: parseFloat(editForm.deposit) || 0,
        project_id: editForm.project_id || null,
        project_name: proj?.project_name || null,
      });
      setEditingId(null);
      loadEntries();
      toast.success('Entry updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleExport = () => {
    let url = `${API_URL}/api/petty-cash/export/pdf`;
    const params = [];
    if (exportStart) params.push(`start_date=${exportStart}`);
    if (exportEnd) params.push(`end_date=${exportEnd}`);
    if (params.length) url += `?${params.join('&')}`;

    const token = localStorage.getItem('token');
    const link = document.createElement('a');
    link.href = url;

    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        link.download = 'petty_cash.pdf';
        link.click();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('PDF exported');
        setShowExport(false);
      })
      .catch(() => toast.error('Export failed'));
  };

  const sorted = [...entries].sort((a, b) => sortAsc ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date));

  const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="border-red-500/20 bg-red-500/5" data-testid="card-total-expenses">
          <CardContent className="p-3 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs font-semibold text-red-400 uppercase tracking-wider">Total Expenses</p>
            <p className="text-base sm:text-xl font-bold text-foreground mt-1">{fmt(totals.total_expenses)}</p>
          </CardContent>
        </Card>
        <Card className="border-green-500/20 bg-green-500/5" data-testid="card-total-deposit">
          <CardContent className="p-3 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs font-semibold text-green-400 uppercase tracking-wider">Total Deposit</p>
            <p className="text-base sm:text-xl font-bold text-foreground mt-1">{fmt(totals.total_deposit)}</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/20 bg-yellow-500/5" data-testid="card-cash-on-hand">
          <CardContent className="p-3 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs font-semibold text-yellow-500 uppercase tracking-wider">Cash on Hand</p>
            <p className={`text-base sm:text-xl font-bold mt-1 ${totals.cash_on_hand < 0 ? 'text-red-500' : 'text-foreground'}`}>
              {fmt(totals.cash_on_hand)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Add Entry Form */}
      <Card className="glass-effect border-border/40">
        <CardHeader className="p-3 sm:p-4 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" /> New Entry
            </CardTitle>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowExport(!showExport)} data-testid="btn-toggle-export">
              <Download className="w-3 h-3 mr-1" /> Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-12 gap-2" data-testid="form-add-entry">
            <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
              className="h-9 text-sm sm:col-span-2" data-testid="input-date" required />
            <Input placeholder="Item description" value={form.item} onChange={e => setForm({...form, item: e.target.value})}
              className="h-9 text-sm sm:col-span-3" data-testid="input-item" required />
            <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm sm:col-span-2" data-testid="select-project">
              <option value="">No Project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
            <Input type="number" step="0.01" min="0" placeholder="Expenses" value={form.expenses}
              onChange={e => setForm({...form, expenses: e.target.value})}
              className="h-9 text-sm sm:col-span-1" data-testid="input-expenses" />
            <Input type="number" step="0.01" min="0" placeholder="Deposit" value={form.deposit}
              onChange={e => setForm({...form, deposit: e.target.value})}
              className="h-9 text-sm sm:col-span-1" data-testid="input-deposit" />
            <label className={`h-9 flex items-center justify-center gap-1 rounded-md border text-xs cursor-pointer sm:col-span-2 ${form.receipt_file ? 'border-green-500/40 bg-green-500/10 text-green-600' : 'border-input bg-background hover:bg-muted/30 text-muted-foreground'}`}
              data-testid="input-receipt-file">
              <Upload className="w-3.5 h-3.5" />
              <span className="truncate">{form.receipt_file ? form.receipt_file.name.slice(0, 12) : 'Receipt'}</span>
              <input type="file" accept=".pdf,image/*" className="hidden"
                onChange={(e) => setForm({...form, receipt_file: e.target.files?.[0] || null})} />
            </label>
            <Button type="submit" size="sm" className="h-9 sm:col-span-1" data-testid="btn-add-entry">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </form>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            <span className="text-destructive font-semibold">*</span> Project and Receipt are required when logging an expense. Deposits do not require either.
          </p>

          {/* Export options */}
          {showExport && (
            <div className="mt-3 p-3 rounded-md border border-border/40 bg-muted/20 flex flex-col sm:flex-row items-end gap-2" data-testid="export-panel">
              <div className="flex-1 w-full sm:w-auto">
                <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                <Input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} className="h-8 text-sm" data-testid="input-export-start" />
              </div>
              <div className="flex-1 w-full sm:w-auto">
                <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                <Input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} className="h-8 text-sm" data-testid="input-export-end" />
              </div>
              <Button size="sm" className="h-8" onClick={handleExport} data-testid="btn-export-pdf">
                <Download className="w-3 h-3 mr-1" /> Download PDF
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card className="glass-effect border-border/40">
        <CardHeader className="p-3 sm:p-4 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" /> Entries ({entries.length})
            </CardTitle>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setSortAsc(!sortAsc)} data-testid="btn-sort-date">
              <ArrowUpDown className="w-3 h-3" /> {sortAsc ? 'Oldest First' : 'Newest First'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-entries">
              <thead>
                <tr className="border-b border-border/40 text-muted-foreground text-xs">
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Item</th>
                  <th className="text-left px-2 py-2 font-medium hidden sm:table-cell">Project</th>
                  <th className="text-right px-3 py-2 font-medium">Expenses</th>
                  <th className="text-right px-3 py-2 font-medium">Deposit</th>
                  <th className="text-center px-2 py-2 font-medium">Receipt</th>
                  <th className="text-center px-2 py-2 font-medium w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No entries yet. Add your first item above.</td></tr>
                ) : sorted.map((entry) => (
                  <tr key={entry.id} className="border-b border-border/20 hover:bg-muted/20 transition-colors" data-testid={`row-${entry.id}`}>
                    {editingId === entry.id ? (
                      <>
                        <td className="px-2 py-1.5">
                          <Input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} className="h-8 text-xs w-32" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input value={editForm.item} onChange={e => setEditForm({...editForm, item: e.target.value})} className="h-8 text-xs" />
                        </td>
                        <td className="px-2 py-1.5 hidden sm:table-cell">
                          <select value={editForm.project_id || ''} onChange={e => setEditForm({...editForm, project_id: e.target.value})}
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs w-full">
                            <option value="">No Project</option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" step="0.01" value={editForm.expenses} onChange={e => setEditForm({...editForm, expenses: e.target.value})} className="h-8 text-xs w-24 text-right" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" step="0.01" value={editForm.deposit} onChange={e => setEditForm({...editForm, deposit: e.target.value})} className="h-8 text-xs w-24 text-right" />
                        </td>
                        <td className="px-2 py-1.5 text-center text-xs text-muted-foreground">—</td>
                        <td className="px-2 py-1.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-500" onClick={() => saveEdit(entry.id)}>
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={cancelEdit}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{entry.date}</td>
                        <td className="px-3 py-2 text-sm">{entry.item}</td>
                        <td className="px-2 py-2 text-xs text-muted-foreground hidden sm:table-cell">
                          {entry.project_name ? <span className="px-2 py-0.5 rounded bg-purple-500/15 text-purple-500 font-medium">{entry.project_name}</span> : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums">
                          {entry.expenses > 0 ? <span className="text-red-400">{fmt(entry.expenses)}</span> : <span className="text-muted-foreground/40">-</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums">
                          {entry.deposit > 0 ? <span className="text-green-400">{fmt(entry.deposit)}</span> : <span className="text-muted-foreground/40">-</span>}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {entry.receipt_url ? (
                            <a href={`${API_URL}${entry.receipt_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary hover:underline text-xs gap-0.5" data-testid={`receipt-link-${entry.id}`}>
                              <Paperclip className="w-3 h-3" /> View
                            </a>
                          ) : (
                            <>
                              <input ref={el => (receiptInputs.current[entry.id] = el)} type="file" accept=".pdf,image/*" className="hidden"
                                onChange={(e) => handleUploadReceipt(entry.id, e.target.files?.[0])} />
                              <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2"
                                onClick={() => receiptInputs.current[entry.id]?.click()} data-testid={`btn-upload-receipt-${entry.id}`}>
                                <Upload className="w-3 h-3 mr-0.5" /> Upload
                              </Button>
                            </>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(entry)} data-testid={`btn-edit-${entry.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(entry.id)} data-testid={`btn-delete-${entry.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
